import type { WorkspacePlan, WorkspaceStatus } from '@/api/types';
import type { BadgeTone } from '@/components/ui';
import { humanize } from '@/lib/format';

/** Shown wherever revenue appears: there is no billing integration yet. */
export const PLAN_MONTHLY_NOTE = 'Estimated: the plan’s monthly list price while the client is active';

export const PLANS: WorkspacePlan[] = ['trial', 'starter', 'growth', 'enterprise'];

export const PLAN_TONE: Record<WorkspacePlan, BadgeTone> = {
  trial: 'neutral',
  starter: 'blue',
  growth: 'brand',
  enterprise: 'accent',
};

export const STATUS_TONE: Record<WorkspaceStatus, BadgeTone> = { active: 'green', suspended: 'red' };

/** Mirrors the backend's slug rule so the field can preview what the server will use. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
}

/** Display names for AI provider kinds and features (unknown values fall back to humanize()). */
export const AI_PROVIDER_LABEL: Record<string, string> = {
  anthropic: 'Anthropic (Claude)',
  openai: 'OpenAI',
  gemini: 'Google Gemini',
  xai: 'xAI (Grok)',
  groq: 'Groq',
  mistral: 'Mistral',
  deepseek: 'DeepSeek',
  openrouter: 'OpenRouter',
  openai_compatible: 'OpenAI-compatible',
};

export const providerLabel = (p: string) => AI_PROVIDER_LABEL[p] ?? humanize(p);

export const AI_FEATURE_LABEL: Record<string, string> = {
  widget_auto_reply: 'Chat widget auto-replies',
  widget_agent_delegate: 'AI agent hand-offs',
  suggest_reply: 'Reply suggestions',
  summarize: 'Ticket summaries',
};

/** "2026-05" → "May 26" */
export function monthLabel(key: string): string {
  return new Intl.DateTimeFormat('en-IN', { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(
    new Date(`${key}-01T00:00:00Z`),
  );
}

export const failureRate = (requests: number, failed: number) =>
  requests === 0 ? null : Math.round((failed / requests) * 1000) / 10;
